import { useCallback, useState, useMemo } from 'react';
import { useActivityRules } from 'ts-activities/useActivityRules';
import { useAsync } from 'react-use';
import {
  NotesRichTextEditor,
  useNotesRichTextEditor,
} from 'components/Inputs/NotesRichTextEditor';
import { NotesLabel } from 'pages/Common/styles/Profile';
import { RequiredAsterisk } from 'components/Inputs/InputControl';

import ActivityService from 'services/ActivityService';
import {
  fieldTypeMapper,
  getFieldsDescription,
} from 'components/Fields/FieldInput/helpers';
import FieldInput from 'components/Fields/FieldInput';
import { isDefaultUndeletableNotesField, isOwnerField } from 'checks/fields';
import { DESCRIPTION_VISIBILITY, FIELD_TYPES } from 'utility/constants';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { KizenTypography } from 'app/typography';
import RelationshipOneV2 from 'components/Fields/FieldInput/RelationshipOneV2';
import ContactSelector from 'components/Modals/ActivityCompletionModal/ContactSelector';
import { TextViewer } from 'components/WYSIWYG';
import { gutters, breakpoints } from 'app/spacing';
import { camelToSnakeCaseKeys } from '__services/helpers';
import { getDataQAForInput } from './Inputs/helpers';
import { AssociationFields } from './GenericWizard/wizards/completeActivity/styled';

export const Section = styled.div`
  margin-bottom: calc(
    ${gutters.spacing(6, -3)}px
  ); // Acounting for room above text in SectionHeader
  &:first-of-type {
    margin-top: ${gutters.spacing(0, -3)}px; // Accounting for descenders
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const FieldLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); // Force equal col widths
  gap: 0px ${gutters.spacing(3)}px;
  margin-bottom: -${gutters.spacing(2)}px; // Account for bottom margin on fields. See components/Fields/FieldInput/index.js

  // half-width components need no styles,
  // full-width components need this class:
  .span2 {
    grid-column: span 2;
  }

  @media (max-width: ${breakpoints.sm}px) {
    // Make all items full-width on small screen sizes
    .span1 {
      grid-column: span 2;
    }
  }

  // Don't display unsupported fields
  > :empty {
    display: none;
  }
`;

export const SectionHeader = styled(KizenTypography)`
  margin-top: 5px;
  margin-bottom: ${gutters.spacing(3, -2)}px; // Descenders
`;

const setIntialValue = ({ fieldType }) => {
  switch (fieldType) {
    case FIELD_TYPES.Checkboxes.type:
    case FIELD_TYPES.Dropdown.type:
      return [];
    default:
      return null;
  }
};

const updateValues = ({ values, field, val }) => ({
  ...values,
  [field.id]: val,
});

const updateErrors = (errors, field) => {
  return (
    errors
      .filter(({ fieldId }) => fieldId !== field.id)
      .map(({ fieldId }) => ({ fieldId })) || []
  );
};

const getLogActivityFieldValues = (values, fields) => {
  if (!values || !fields) {
    return [];
  }

  return fields.map((field) => {
    return { ...field, value: values[field.id] };
  });
};

/**
 * @typedef {import('pages/ActivitySettings/types').ActivityState} ActivityState
 * @typedef {{
 *  customObjectId: string;
 *  field: ActivityState['customObjects'][number];
 *  isReadonly: boolean;
 *  isRequired: boolean
 * }} AssociationData
 * @param {ActivityState} activity
 * @returns {AssociationData[]}
 */
const getAssociationData = (activity) => {
  if (activity.associationMode === 'selected_objects_associated') {
    return activity.associatedObjects
      .toSorted((a, b) => {
        return a.order - b.order;
      })
      .map((ao) => {
        const field = activity.customObjects.find(
          (co) => co.id === ao.customObject.id
        );

        return {
          customObjectId: ao.customObject.id,
          field,
          isReadonly: ao.isReadonly,
          isRequired: ao.isRequired,
        };
      });
  }

  if (activity.associationMode === 'all_objects_associated') {
    return activity.customObjects.map((co) => {
      return {
        customObjectId: co.id,
        field: co,
        isReadonly: false,
        isRequired: false,
      };
    });
  }

  return [];
};

const ActivityForm = ({ activity }) => {
  const { t } = useTranslation();
  const { id: activityObjectId, visibilityRules } = activity;
  const [values, setValues] = useState({});
  const [associationValues, setAssociationValues] = useState({});
  const [errors, setErrors] = useState([]);
  const [initFields, setInitFields] = useState([]);
  const { notes, handleNoteChange, editorRef } = useNotesRichTextEditor('');

  const fieldModifier = useCallback(
    (field) => {
      if (field && isOwnerField(field)) {
        const initFieldObj = initFields.find(({ id }) => id === field.id);
        field.isRequired = initFieldObj.isRequired;
      }

      if (field.customObjectField?.meta?.isMasked) {
        field.meta = {
          ...field.meta,
          isMasked: true,
        };
      }
      if (field.customObjectField?.meta?.isMarkdown) {
        field.meta = {
          ...field.meta,
          isMarkdown: true,
        };
      }

      return field;
    },
    [initFields]
  );

  const handleChangeField = useCallback(
    (val, field) => {
      setErrors(updateErrors(errors, field));
      setValues(
        updateValues({
          values,
          field,
          val,
        })
      );
    },
    [errors, values, setErrors]
  );

  const { value: fields } = useAsync(async () => {
    let rawFields = await ActivityService.getObjectFields({
      activityObjectId,
    });
    // faking access & and munging the fieldType for custom fields
    rawFields = rawFields.reduce((collect, f) => {
      let { fieldType } = f;

      // if custom field try and modelfield
      if (fieldType === FIELD_TYPES.ActivityCustomField.type) {
        const {
          fieldType: orgFieldType = FIELD_TYPES.Text.type,
          isDefault: orgIsDefault = false,
          name: orgName = '',
        } = f.customObjectField;
        fieldType = fieldTypeMapper({
          isDefault: orgIsDefault,
          name: orgName,
          fieldType: orgFieldType,
        });
      }
      collect = [...collect, { ...f, fieldType, access: { edit: true } }];

      return collect;
    }, []);
    // initialize some values for each field
    const initialValues = rawFields.reduce(
      (acc, f) => ({
        ...acc,
        [f.id]: setIntialValue(f),
      }),
      {}
    );
    setValues(initialValues); // one for tracking
    setInitFields([...rawFields]);

    return rawFields;
  }, [activityObjectId]);

  const fieldsById = useMemo(() => {
    if (!fields?.length) {
      return {};
    }

    return fields.reduce((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});
  }, [fields]);

  const errorsMap = useMemo(() => {
    const errorsMap = {};
    if (fields?.length) {
      fields.forEach((field) => {
        const error = errors.find((item) => item.fieldId === field.id);
        errorsMap[field.id] = error?.message
          ? { message: error.message }
          : null;
      });
    }
    return errorsMap;
  }, [fields, errors]);

  const { isRuleActiveForLayoutItem } = useActivityRules(
    camelToSnakeCaseKeys(visibilityRules),
    getLogActivityFieldValues(values, fields)
  );

  const associationData = getAssociationData(activity);

  if (!fields?.length) {
    return null;
  }

  return (
    <Section>
      {associationData.length > 0 && (
        <>
          <SectionHeader type="header">
            {t('Verify Associations')}
          </SectionHeader>
          <AssociationFields>
            {associationData.map((association) => {
              const { customObjectId, field, isReadonly, isRequired } =
                association;

              if (field.name === 'client_client') {
                return (
                  <ContactSelector
                    margin
                    field={field}
                    key={customObjectId}
                    disableTopMenuButton
                    label={field.objectName}
                    isReadOnly={isReadonly}
                    disabled={isReadonly}
                    required={isRequired}
                    value={associationValues[field.id]}
                    onChange={(value) => {
                      setAssociationValues((prev) => {
                        return { ...prev, [field.id]: value };
                      });
                    }}
                  />
                );
              }

              return (
                <RelationshipOneV2
                  margin
                  key={customObjectId}
                  disableTopMenuButton
                  label={field.objectName}
                  field={{
                    ...field,
                    relations: [
                      {
                        fetchUrl: `field-model-custom`,
                        model: field.id,
                        modelFetchUrl: field.fetchUrl,
                        objectName: field.objectName,
                      },
                    ],
                  }}
                  isReadOnly={isReadonly}
                  disabled={isReadonly}
                  required={isRequired}
                  value={associationValues[field.id]}
                  onChange={(value) => {
                    setAssociationValues((prev) => {
                      return { ...prev, [field.id]: value };
                    });
                  }}
                />
              );
            })}
          </AssociationFields>
        </>
      )}
      <SectionHeader type="header">{t('Complete Activity')}</SectionHeader>
      <FieldLayout>
        {activity.layout.map((item) => {
          if (item.type === 'field') {
            const field = fieldsById[item.fieldId];

            if (
              !field ||
              field.isHidden ||
              !isRuleActiveForLayoutItem(item.fieldId)
            ) {
              return null;
            }

            // There are no prepopulated values - this is here to showcase the
            // disabled logic that exists on the actual complete activity modal
            const hasInitialValue = false;
            const disableInput =
              !hasInitialValue && field.isRequired && field.meta.isReadonly
                ? false
                : field.meta.isReadonly;

            let fieldTypeProps = {};

            if (field.fieldType === 'longtext') {
              fieldTypeProps = {
                initialHeight: 106, // 5 lines of text
              };
            }
            const { withNameDescription } = getFieldsDescription(field, [
              DESCRIPTION_VISIBILITY[0].value,
              DESCRIPTION_VISIBILITY[1].value,
            ]);
            const additionalProps = {
              labelInfo: withNameDescription,
              description: withNameDescription,
            };
            return (
              <div
                key={item.id}
                className={item.columns === 2 ? 'span2' : 'span1'}
              >
                {isDefaultUndeletableNotesField(field) ? (
                  <NotesRichTextEditor
                    inModal
                    ref={editorRef}
                    label={
                      <NotesLabel>
                        {t('Notes')}
                        {field.isRequired && (
                          <RequiredAsterisk>*</RequiredAsterisk>
                        )}
                      </NotesLabel>
                    }
                    value={notes}
                    onChange={handleNoteChange}
                    margin
                  />
                ) : (
                  <FieldInput
                    margin
                    portalDateInput
                    field={fieldModifier(field)}
                    fetchUrl="field-model-custom"
                    disabled={disableInput}
                    value={values[field.id]}
                    onChange={(val) => handleChangeField(val, field)}
                    isGetFullObject
                    underline={false}
                    error={errorsMap[field.id]}
                    serviceToUse={ActivityService}
                    objectId={activityObjectId}
                    required={field.isRequired}
                    isShowAsterisk={field.isRequired}
                    inModal
                    {...getDataQAForInput(
                      `activity-${field.name}`,
                      field.fieldType
                    )}
                    {...fieldTypeProps}
                    {...additionalProps}
                  />
                )}
              </div>
            );
          }

          if (!isRuleActiveForLayoutItem(item.internalName)) {
            return null;
          }

          return (
            <div
              key={item.id}
              style={{ marginBottom: '20px' }}
              className={item.columns === 2 ? 'span2' : 'span1'}
            >
              <TextViewer content={item.label} />
            </div>
          );
        })}
      </FieldLayout>
    </Section>
  );
};

export default ActivityForm;
